import { useRef, useEffect, type ReactNode } from 'react'

/**
 * Headless listbox half of the WAI-ARIA 1.2 combobox/listbox pattern.
 * Pairs with GlobalSearchCombobox. Handles scroll-into-view for the active option.
 * Focus stays on the combobox input via aria-activedescendant.
 */
interface IGlobalSearchListboxProps {
  id: string
  ariaLabel: string
  highlightedIndex: Nullable<number>
  children: ReactNode
}

export const GlobalSearchListbox = ({
  id,
  ariaLabel,
  highlightedIndex,
  children,
}: IGlobalSearchListboxProps) => {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlightedIndex === null) return
    const options =
      listRef.current?.querySelectorAll<HTMLElement>('[role="option"]')
    const highlighted = options?.[highlightedIndex]
    highlighted?.scrollIntoView?.({ block: 'nearest' })
  }, [highlightedIndex])

  return (
    // role="listbox" on a div is the correct WAI-ARIA pattern for a custom combobox.
    // <select>/<datalist> cannot render the custom content (icons, metadata, status labels) this component requires.
    <div id={id} role='listbox' aria-label={ariaLabel} ref={listRef}>
      {children}
    </div>
  )
}
